import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardCheck, faPlus, faSearch, faFolder, faChevronDown, faChevronUp, faCheck, faArrowLeft, faTrash, faThumbtack, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import DataTable, { createActionColumn } from './DataTable';
import StatusBadge from './StatusBadge';
import './Pages.css';
import './CampaignQAPage.css';

/**
 * CampaignQAPage component for managing Campaign QA validation jobs
 * Validates DV360 campaigns against their original Excel launch templates
 */
const CampaignQAPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Folder filter state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);

  // Pagination state
  const getPersistedPaginationState = () => {
    const urlFolderId = searchParams.get('folderId');
    try {
      const saved = sessionStorage.getItem('campaignQAPage_paginationState');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (urlFolderId) parsed.selectedFolderId = urlFolderId;
        return parsed;
      }
    } catch (err) {
      console.error('Error loading pagination state:', err);
    }
    return {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: 'created_at',
      sortOrder: 'desc',
      searchTerm: '',
      selectedFolderId: urlFolderId || null
    };
  };

  const initialState = getPersistedPaginationState();

  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialState.itemsPerPage);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  // Search-as-you-type against GET /api/v2/campaign-qa — debounced through the
  // shared typeahead hook so a burst of keystrokes fetches only the final
  // term. The raw searchTerm still persists to sessionStorage instantly.
  const debouncedSearchTerm = useDebouncedValue(searchTerm);
  const [selectedFolderId, setSelectedFolderId] = useState(initialState.selectedFolderId);
  const [pagination, setPagination] = useState({
    currentPage: initialState.currentPage,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: initialState.itemsPerPage,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRefs = useRef({});

  // Persist pagination state
  const persistPaginationState = (state) => {
    try {
      sessionStorage.setItem('campaignQAPage_paginationState', JSON.stringify(state));
    } catch (err) {
      console.error('Error saving pagination state:', err);
    }
  };

  useEffect(() => {
    persistPaginationState({ currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, selectedFolderId });
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, selectedFolderId]);

  // Fetch folders when account changes
  useEffect(() => {
    if (accountId && user) fetchFolders();
  }, [accountId, user]);

  // Fetch jobs when filters change (search rides the debounced term, so the
  // fetch fires once the user stops typing)
  useEffect(() => {
    if (accountId && user) fetchJobs();
  }, [accountId, user, currentPage, itemsPerPage, sortBy, sortOrder, debouncedSearchTerm, selectedFolderId]);

  const fetchJobs = async () => {
    if (!accountId || !user) return;
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        accountId,
        userId: user?.userId || user?.id,
        search: debouncedSearchTerm,
        sortBy,
        sortOrder,
        page: currentPage,
        limit: itemsPerPage
      };
      if (selectedFolderId) params.folderId = selectedFolderId;

      const response = await api.get(getApiUrl('/api/v2/campaign-qa'), { params });
      const jobsData = response.data.jobs || [];

      // Sort with pinned items first
      jobsData.sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        return 0;
      });

      setJobs(jobsData);
      if (response.data.pagination) {
        setPagination({
          currentPage: response.data.pagination.page,
          totalPages: response.data.pagination.totalPages,
          totalItems: response.data.pagination.total,
          itemsPerPage: response.data.pagination.limit,
          hasNextPage: response.data.pagination.hasNextPage,
          hasPrevPage: response.data.pagination.hasPrevPage
        });
      }
    } catch (err) {
      console.error('Error fetching campaign QA jobs:', err);
      setError(err.response?.data?.error || 'Failed to load campaign QA jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    if (!accountId || !user) return;
    try {
      setFoldersLoading(true);
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: { accountId, userId: user?.userId || user?.id, limit: 100 }
      });
      const foldersData = response.data.folders || response.data || [];
      setFolders(foldersData);
      if (selectedFolderId && !selectedFolder) {
        const folder = foldersData.find(f => f.id === selectedFolderId);
        if (folder) setSelectedFolder(folder);
        else { setSelectedFolderId(null); setSelectedFolder(null); }
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortBy === key && sortOrder === 'desc') direction = 'asc';
    setSortBy(key);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleFolderSelect = (folder) => {
    if (folder) {
      setSelectedFolder(folder);
      setSelectedFolderId(folder.id);
      setSearchParams({ folderId: folder.id });
    } else {
      setSelectedFolder(null);
      setSelectedFolderId(null);
      setSearchParams({});
    }
    setCurrentPage(1);
    setShowFolderDropdown(false);
  };

  const toggleDropdown = (jobId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (openDropdownId === jobId) { setOpenDropdownId(null); return; }
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = 160;
    const dropdownHeight = 80;
    let top = buttonRect.bottom + 5;
    let left = buttonRect.right - dropdownWidth;
    if (left < 10) left = 10;
    if (top + dropdownHeight > window.innerHeight - 10) top = buttonRect.top - dropdownHeight - 5;
    setDropdownPosition({ top, left });
    setOpenDropdownId(jobId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !Object.values(dropdownRefs.current).some(ref => ref && ref.contains(event.target))) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Close folder dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFolderDropdown && !event.target.closest('.folder-filter-container')) {
        setShowFolderDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFolderDropdown]);

  const formatDate = (job) => {
    const date = job.createdAt;
    if (!date) return 'Unknown';
    const jobDate = new Date(date);
    const now = new Date();
    const isToday = jobDate.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = jobDate.toDateString() === yesterday.toDateString();
    if (isToday) return `Today ${jobDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    if (isYesterday) return `Yesterday ${jobDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    const diffTime = Math.abs(now - jobDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays <= 7) return `${diffDays} days ago`;
    return jobDate.toLocaleDateString();
  };

  // Get validation result summary
  const getValidationSummary = (job) => {
    if (job.status === 'pending' || job.status === 'parsing' || job.status === 'processing') return null;
    return {
      passed: job.validationPassed,
      total: job.totalDiscrepancies || 0,
      high: job.highSeverityCount || 0,
      medium: job.mediumSeverityCount || 0,
      low: job.lowSeverityCount || 0
    };
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      width: '1fr',
      sortable: true,
      resizable: true,
      render: (job) => (
        <div className="campaign-name-content">
          {job.isPinned && <FontAwesomeIcon icon={faThumbtack} className="pinned-icon" title="Pinned job" />}
          <span className="campaign-name-text" title={job.name}>{job.name}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      sortable: true,
      render: (job) => <StatusBadge status={job.status} />
    },
    {
      key: 'validation',
      label: 'Validation',
      width: '120px',
      render: (job) => {
        const summary = getValidationSummary(job);
        if (!summary) return <span className="text-muted">-</span>;
        return (
          <span className={`validation-result ${summary.passed ? 'passed' : 'failed'}`}>
            <FontAwesomeIcon icon={summary.passed ? faCheckCircle : faTimesCircle} />
            {summary.passed ? 'Passed' : 'Failed'}
          </span>
        );
      }
    },
    {
      key: 'platform',
      label: 'Platform',
      width: '100px',
      render: (job) => (
        <span className={`platform-badge ${job.platform}`}>
          {job.platform === 'dv360' ? 'DV360' : job.platform === 'facebook' ? 'Facebook' : job.platform}
        </span>
      )
    },
    {
      key: 'folder_name',
      label: 'Folder',
      width: '180px',
      sortable: true,
      render: (job) => (
        <span className="folder-info">
          <FontAwesomeIcon icon={faFolder} />
          {job.folderName || 'No folder'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      width: '180px',
      sortable: true,
      render: (job) => formatDate(job)
    },
    createActionColumn(null, dropdownRefs, openDropdownId, toggleDropdown)
  ];

  // Dropdown Menu Component
  const DropdownMenu = ({ job, onClose }) => {
    if (!job) return null;

    const handlePin = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      try {
        const response = await api.patch(getApiUrl(`/api/v2/campaign-qa/${job.id}`), {
          is_pinned: !job.isPinned
        }, { params: { accountId, userId: user?.userId || user?.id } });

        if (response.status === 200) {
          const updatedJobs = jobs.map(j => j.id === job.id ? { ...j, isPinned: !j.isPinned } : j);
          updatedJobs.sort((a, b) => {
            if (a.isPinned && !b.isPinned) return -1;
            if (!a.isPinned && b.isPinned) return 1;
            return 0;
          });
          setJobs(updatedJobs);
        }
      } catch (err) {
        console.error('Error updating pin status:', err);
        setError('Failed to update job. Please try again.');
      }
      onClose();
    };

    const handleDelete = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (window.confirm(`Are you sure you want to delete "${job.name}"?`)) {
        try {
          await api.delete(getApiUrl(`/api/v2/campaign-qa/${job.id}`), {
            params: { accountId, userId: user?.userId || user?.id }
          });
          setJobs(prev => prev.filter(j => j.id !== job.id));
          setPagination(prev => ({ ...prev, totalItems: Math.max(0, prev.totalItems - 1) }));
        } catch (err) {
          console.error('Error deleting job:', err);
          setError('Failed to delete job. Please try again.');
        }
      }
      onClose();
    };

    return (
      <div className="sidebar-dropdown-menu" style={{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }} onClick={(e) => e.stopPropagation()}>
        <button onMouseDown={handlePin} className="sidebar-dropdown-item">
          <FontAwesomeIcon icon={faThumbtack} />
          {job.isPinned ? 'Unpin' : 'Pin'}
        </button>
        <button onMouseDown={handleDelete} className="sidebar-dropdown-item delete">
          <FontAwesomeIcon icon={faTrash} />
          Delete
        </button>
      </div>
    );
  };

  if (!accountId) {
    return (
      <div className="page-container">
        <div className="page-error">
          <p>Please select an account to view campaign QA jobs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          {selectedFolder && (
            <div className="breadcrumb-nav">
              <button onClick={() => handleFolderSelect(null)} className="breadcrumb-back-btn">
                <FontAwesomeIcon icon={faArrowLeft} />
                All Folders
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-current">{getFolderDisplayLabel(selectedFolder)}</span>
            </div>
          )}
          <h1>
            Campaign QA
            {selectedFolder && <span className="folder-context">in {getFolderDisplayLabel(selectedFolder)}</span>}
            <span className="count-badge">{pagination.totalItems || jobs.length}</span>
          </h1>
          <p>
            {selectedFolder
              ? `QA validation jobs in the "${getFolderDisplayLabel(selectedFolder)}" folder`
              : 'Validate campaigns against launch templates and media plans'
            }
          </p>
        </div>
        <div className="header-right">
          {/* Folder Filter */}
          <div className="folder-filter-container">
            <button type="button" className={`folder-filter-button ${showFolderDropdown ? 'active' : ''}`} onClick={() => setShowFolderDropdown(!showFolderDropdown)} disabled={foldersLoading}>
              <FontAwesomeIcon icon={faFolder} />
              <span>{selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'All Folders'}</span>
              <FontAwesomeIcon icon={showFolderDropdown ? faChevronUp : faChevronDown} className="dropdown-arrow" />
            </button>
            {showFolderDropdown && (
              <div className="folder-filter-dropdown">
                <button type="button" className={`folder-filter-option ${!selectedFolder ? 'selected' : ''}`} onClick={() => handleFolderSelect(null)}>
                  <FontAwesomeIcon icon={faFolder} />
                  <span>All Folders</span>
                </button>
                <div className="folder-filter-divider" />
                {folders.map(folder => (
                  <button key={folder.id} type="button" className={`folder-filter-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`} onClick={() => handleFolderSelect(folder)}>
                    <FontAwesomeIcon icon={faFolder} />
                    <span>{getFolderDisplayLabel(folder)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input type="text" placeholder="Search jobs..." value={searchTerm} onChange={handleSearchChange} className="search-input" />
          </div>

          <button className="btn btn-primary" onClick={() => navigate('/campaign-qa/new')}>
            <FontAwesomeIcon icon={faPlus} />
            New QA Job
          </button>
        </div>
      </div>

      {error && <div className="page-error"><p>{error}</p></div>}

      <DataTable
        data={jobs}
        columns={columns}
        pagination={pagination}
        onSort={requestSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onRowClick={(job, e) => {
          if (e.target.closest('.dropdown-container') || e.target.closest('.sidebar-action-button')) return;
          navigate(`/campaign-qa/${job.id}`);
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isLoading}
        emptyMessage={
          <div className="empty-state">
            <FontAwesomeIcon icon={faClipboardCheck} className="empty-state-icon" />
            <h3>No QA jobs yet</h3>
            <p>Create your first QA job to validate a campaign</p>
          </div>
        }
        className="campaign-qa-table"
        storageKey="campaign-qa"
      />

      {openDropdownId && <DropdownMenu job={jobs.find(j => j.id === openDropdownId)} onClose={() => setOpenDropdownId(null)} />}
    </div>
  );
};

export default CampaignQAPage;
